import { Controller } from "@hotwired/stimulus"

// Opens the shared modal shell (app/views/dashboard/_modal.html.erb) with content
// fetched from a URL. Used by the catalog source counts "Show" button, which pulls
// the per-source in-stock trend chart from /dashboard/catalog_source_trend.
//
// Button contract:
//   data-action="click->remote-modal#open"
//   data-remote-modal-url="/dashboard/catalog_source_trend?source=AMZ_US"
//   data-remote-modal-title="In stock trend — AMZ_US"
// Fetched fragments may re-request themselves with another window via:
//   data-action="click->remote-modal#switchWindow" data-remote-modal-hours="24"
export default class extends Controller {
  static targets = ["title", "body", "dialog"]

  connect() {
    this.url = ""
    this.requestId = 0
    this.onKeydown = (event) => {
      if (event.key === "Escape" && this.isOpen) this.close()
    }
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
  }

  get isOpen() {
    return this.dialogTarget.classList.contains("is-open")
  }

  open(event) {
    const button = event.currentTarget
    this.titleTarget.textContent = button.dataset.remoteModalTitle || "Details"
    this.url = button.dataset.remoteModalUrl || ""
    // The controller scope wraps the panel frame + modal shell, so the visible
    // dialog is addressed through its target rather than this.element.
    this.dialogTarget.classList.add("is-open")
    document.body.classList.add("has-modal")
    this.load(this.url)
  }

  // Re-render the same chart for another lookback window (24h / 7d).
  switchWindow(event) {
    const hours = event.currentTarget.dataset.remoteModalHours
    if (!this.url || !hours) return

    this.load(this.urlWithHours(hours))
  }

  close() {
    this.dialogTarget.classList.remove("is-open")
    document.body.classList.remove("has-modal")
  }

  urlWithHours(hours) {
    const url = new URL(this.url, window.location.origin)
    url.searchParams.set("hours", hours)
    return `${url.pathname}${url.search}`
  }

  async load(url) {
    if (!url) return

    const requestId = ++this.requestId
    this.bodyTarget.innerHTML = '<p class="dashboard-modal-loading color-fg-muted">Loading…</p>'

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      // Auth redirects would otherwise inject the login page into the modal.
      if (response.redirected) throw new Error("session expired — reload the dashboard")

      const html = await response.text()
      // Ignore stale responses (user clicked another window/source meanwhile).
      if (requestId !== this.requestId || !this.isOpen) return

      this.bodyTarget.innerHTML = html
    } catch (error) {
      if (requestId !== this.requestId) return

      this.bodyTarget.innerHTML =
        `<div class="Banner Banner--error p-3 f6">Failed to load trend: ${this.escapeHtml(error.message)}</div>`
    }
  }

  escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[char]))
  }
}
